import { create } from 'zustand';
import {
  ConvertTableAnswerData,
  FormRequestData,
  InternalAnswerSet,
  InternalTableAnswerSet,
  JpaAnswer,
  JpaBasicInfo,
  JpaContactAnswer,
  JpaRequestAnswer,
  JpaTableAddedRowCounts,
  JpaTableAnswer,
  JpaTableAnswerRow,
  JpaValueType,
} from '@api/services/jpa/types';
import { convertApiAnswerData, formatContactAnswers } from '@api/services/jpa/helpers';
import { QUESTION_TYPE } from '@components/data-driven-form/ddf.validation';
import { SelectInputOption } from '@api/types';
import _cloneDeep from 'lodash/cloneDeep';
import _set from 'lodash/set';
import _uniqueId from 'lodash/uniqueId';
import { TableInputOnChangeData } from '@components/table-input/tableInput.types';
import { JpaTableInputRowData } from '@components/table-input/jpaTableInput.types';
import { produce } from 'immer';
import { queryClient } from '@api/queryClient';
import { JPA_QUERY_KEYS } from '@api/services/jpa';
import { getAppStore } from '@src/utils/store-reference';
import { sanitizeText } from '@src/utils/helpers';

interface JpaStoreValues {
  answerSet: InternalAnswerSet; // NOTE: internally formatted answers
  tableAddedRowCounts: JpaTableAddedRowCounts;
  defaultValues: { [key: string]: JpaValueType | undefined }; // NOTE: react-hook-form answers
  formRequestData: FormRequestData;
  fullQuestionIds: number[];
  jpaBasicInfo: JpaBasicInfo;
  jpaContactAnswerSet: JpaContactAnswer[];
  jpaOrgIds: number[];
  jpaQuestionText: { [jpaQuestionID: number]: string };
  jpaQuestionVisibility: { [jpaQuestionID: number]: boolean };
  jpaRequestAnswerSets: JpaRequestAnswer[]; // NOTE: coming from API response
  tableAnswerSet: InternalTableAnswerSet;
  validPermitTypes: string[];
}

interface RepaginateTableAnswers {
  formId: number;
  itemsPerPage: number;
  pageCount: number;
  questionId: number;
}

interface JpaState extends JpaStoreValues {
  clearAnswers: (formId: number) => void;
  clearOrphanedAnswerRows: (formId: number, questionId: number, totalRowCount: number) => void;
  convertTableAnswerData: ({
    columnData,
    formId,
    onComplete,
    pageNumber,
    questionId,
    tableAnswerData,
  }: ConvertTableAnswerData) => void;
  removeDeletedTableAnswers: () => void;
  repaginateTableAnswers: ({ formId, questionId, pageCount, itemsPerPage }: RepaginateTableAnswers) => void;
  resetToDefaults: () => void;
  setAnswer: (val: JpaValueType, questionId: number, questionTypeId: number, formId: number) => void;
  setApiAnswers: () => void;
  setFullQuestionIds: (fullQuestionIds: number[]) => void;
  setContactAnswer: (contactAnswers: JpaContactAnswer[], questionId: number) => void;
  setJpaOrgIds: (jpaOrgIds: number[]) => void;
  setQuestionText: (questionId: number, questionText: string) => void;
  setQuestionVisibility: (questionId: number, isVisible: boolean) => void;
  setTableInputAnswer: (data: TableInputOnChangeData, questionId: number, formId: number) => void;
}

const defaults: JpaStoreValues = {
  answerSet: {},
  defaultValues: {},
  formRequestData: {},
  fullQuestionIds: [],
  jpaBasicInfo: [],
  jpaContactAnswerSet: [],
  jpaOrgIds: [],
  jpaQuestionText: {},
  jpaQuestionVisibility: {},
  jpaRequestAnswerSets: [],
  tableAddedRowCounts: {},
  tableAnswerSet: {},
  validPermitTypes: [],
};

const questionTypeMap: { [key: number]: string } = {
  1: 'jpaShortAnswerText',
  2: 'jpaFullAnswerText',
  3: 'jpaAnswerDate',
  4: 'jpaAnswerNumber',
  5: 'jpaAnswerYesNo',
  6: 'hasJPAFileUploaded',
  7: 'jpaListValues',
};

export const useJpa = create<JpaState>((set, get) => ({
  ..._cloneDeep(defaults),

  clearAnswers: (formId) => {
    const jpaQuestionIDs = get().fullQuestionIds;

    const answerSet = _cloneDeep(get().answerSet);
    const tableAnswerSet = _cloneDeep(get().tableAnswerSet);
    let jpaContactAnswerSet = _cloneDeep(get().jpaContactAnswerSet);

    // Clear answers in the answer set

    jpaQuestionIDs?.forEach((jpaQuestionID) => {
      // delete file from S3 if uploaded
      if (answerSet[formId]?.[jpaQuestionID]?.hasJPAFileUploaded) {
        const store = getAppStore();
        store.doResetJPAFiles(jpaQuestionID.toString(), true);
      }
      if (answerSet[formId]?.[jpaQuestionID]) {
        delete answerSet[formId][jpaQuestionID];
      }
      if (answerSet[formId] && Object.keys(answerSet[formId]).length === 0) {
        delete answerSet[formId];
      }
    });
    // Clear answers in the table answer set
    const tableKey = `f${formId}`;
    if (tableAnswerSet[tableKey]) {
      jpaQuestionIDs?.forEach((jpaQuestionID) => {
        const questionKey = `q${jpaQuestionID}`;
        if (tableAnswerSet[tableKey][questionKey]) {
          delete tableAnswerSet[tableKey][questionKey];
        }
      });
    }

    // Clear answers in the contact answer set
    if (jpaContactAnswerSet.length > 0) {
      jpaContactAnswerSet = jpaContactAnswerSet.filter((contact) => !jpaQuestionIDs.includes(contact.questionID));
    }

    set({ answerSet, tableAnswerSet, jpaContactAnswerSet });
  },

  clearOrphanedAnswerRows: (formId, questionId, totalRowCount) => {
    const tableAnswerSet = get().tableAnswerSet;
    const formKey = `f${formId}`;
    const questionKey = `q${questionId}`;
    const questionAnswerSet = tableAnswerSet[formKey]?.[questionKey];

    if (totalRowCount === 0 || questionAnswerSet === null || questionAnswerSet === undefined) {
      return;
    }
    const rowCount = { value: 0 };

    // loop thorough each page of the table answer set
    Object.keys(tableAnswerSet[formKey][questionKey]).forEach((pageKey) => {
      const numPageRows = tableAnswerSet[formKey][questionKey][pageKey]?.length;
      const deleteRowIndexes: number[] = [];

      // loop through each row of the page
      Object.keys(tableAnswerSet[formKey][questionKey][pageKey]).forEach((_, rowIndex) => {
        rowCount.value += 1;

        // if the row count is greater than the total row count, mark the row for deletion
        if (rowCount.value > totalRowCount) {
          deleteRowIndexes.push(rowIndex);
        }
      });

      // delete the marked rows
      deleteRowIndexes.sort((a, b) => b - a); // sort in descending order to avoid index shift
      deleteRowIndexes.forEach((rowIndex) => {
        set(
          produce((draft) => {
            draft.tableAnswerSet[formKey][questionKey][pageKey].splice(rowIndex, 1);
          })
        );
      });

      // if all rows on the page are deleted, delete the page
      if (deleteRowIndexes.length === numPageRows) {
        set(
          produce((draft) => {
            delete draft.tableAnswerSet[formKey][questionKey][pageKey];
          })
        );
      }
    });
  },

  convertTableAnswerData: ({
    columnData,
    formId,
    onComplete,
    pageNumber,
    questionId,
    tableAnswerData,
  }: ConvertTableAnswerData) => {
    const tableAnswerSet: InternalTableAnswerSet = useJpa.getState().tableAnswerSet;
    const newTableAnswerSet = _cloneDeep(tableAnswerSet);

    if (tableAnswerData?.length > 0) {
      tableAnswerData?.forEach((tableAnswerRow: JpaTableAnswerRow, rowIndex) => {
        const rowNumber = tableAnswerRow.find((row) => row?.jpaDynRowNumber)?.jpaDynRowNumber;

        tableAnswerRow.forEach((tableAnswer: JpaTableAnswer, cellIndex) => {
          const rowPathArr = [`f${formId}`, `q${questionId}`, `p${pageNumber}`, rowIndex, cellIndex];
          const typeId = columnData[cellIndex].jpaColumnType;
          let value = null;

          if (tableAnswer !== null) {
            if (tableAnswer?.jpaDynListValues !== null) {
              value = tableAnswer.jpaDynListValues;
            } else if (tableAnswer?.jpaDynAnswerYesNo !== null) {
              value = !!tableAnswer.jpaDynAnswerYesNo;
            } else if (tableAnswer?.jpaDynAnswerDate !== null) {
              value = tableAnswer.jpaDynAnswerDate;
            } else if (tableAnswer?.jpaDynAnswerNumber !== null) {
              value = tableAnswer.jpaDynAnswerNumber;
            } else {
              value = tableAnswer.jpaDynShortAnswerText;
            }
          }

          // set the table cell value
          _set(newTableAnswerSet, rowPathArr, {
            answerId: tableAnswer?.jpaDynTableAnswerID || 0,
            isDeleted: false,
            questionId,
            recordId: _uniqueId(),
            rowNumber: rowNumber,
            typeId,
            value,
          });
        });
      });
    }

    useJpa.setState({
      tableAnswerSet: newTableAnswerSet,
    });

    onComplete();
  },

  resetToDefaults: () => set(_cloneDeep(defaults)),

  removeDeletedTableAnswers: () => {
    set(
      produce((draft) => {
        Object.keys(draft.tableAnswerSet).forEach((formKey) => {
          Object.keys(draft.tableAnswerSet[formKey]).forEach((questionKey) => {
            Object.keys(draft.tableAnswerSet[formKey][questionKey]).forEach((pageKey) => {
              draft.tableAnswerSet[formKey][questionKey][pageKey] = draft.tableAnswerSet[formKey][questionKey][
                pageKey
              ].filter((row: JpaTableInputRowData) => !row[0]?.isDeleted);
            });
          });
        });
      })
    );
  },

  // repaginate table rows when added and unsaved rows span multiple pages
  repaginateTableAnswers: ({ formId, questionId, pageCount, itemsPerPage }) => {
    const formKey = `f${formId}`;
    const questionKey = `q${questionId}`;

    const tableRows = Object.keys(get().tableAnswerSet[formKey][questionKey]).reduce(
      (acc: JpaTableInputRowData[], pageKey) => {
        const rows = get().tableAnswerSet[formKey][questionKey][pageKey];
        return acc.concat(rows);
      },
      []
    );

    // loop through pagers and repaginate rows
    for (let i = 0; i < pageCount; i++) {
      const pageKey = `p${i + 1}`;
      const startIndex = i * itemsPerPage;
      const pageRows = tableRows.slice(startIndex, startIndex + itemsPerPage);

      if (pageRows.length === 0) {
        set(
          produce((draft) => {
            delete draft.tableAnswerSet[formKey][questionKey][pageKey];
          })
        );
      } else {
        set(
          produce((draft) => {
            draft.tableAnswerSet[formKey][questionKey][pageKey] = pageRows;
          })
        );
      }
    }
  },

  setAnswer: (val, questionId, questionTypeId, formId) => {
    let typeValue: JpaValueType = val;
    if (Array.isArray(val)) {
      if (questionTypeId === QUESTION_TYPE.option) {
        typeValue = val.map((item: SelectInputOption) => String(item.value)).join(';');
      }
      if (questionTypeId === QUESTION_TYPE.file) {
        typeValue = val.length > 0 ? true : false;
      }
    }

    const cleanValue = typeof typeValue === 'string' ? sanitizeText(typeValue).trim() : typeValue;

    const answerObj: JpaAnswer = {
      jpaQuestionID: questionId,
      jpaQuestionTypeID: questionTypeId,
      [questionTypeMap[questionTypeId]]: cleanValue,
    };

    set(
      produce((draft) => {
        if (draft.answerSet[formId]?.[questionId]) {
          const question = draft.answerSet?.[formId]?.[questionId] || {};

          draft.answerSet[formId][questionId] = {
            ...question,
            ...answerObj,
          };
        } else if (draft.answerSet[formId]) {
          draft.answerSet[formId][questionId] = {
            ...answerObj,
          };
        } else {
          draft.answerSet[formId] = {
            [questionId]: {
              ...answerObj,
            },
          };
        }
      })
    );
  },

  setApiAnswers: () => {
    const jpaRequestAnswerSets = get().jpaRequestAnswerSets;
    const { formattedAnswerSet, formRequestData, rhfDefaultValues } = convertApiAnswerData(jpaRequestAnswerSets);
    const formattedContactAnswers = formatContactAnswers(jpaRequestAnswerSets);

    set({
      answerSet: formattedAnswerSet,
      defaultValues: rhfDefaultValues,
      formRequestData,
      jpaContactAnswerSet: formattedContactAnswers,
      tableAddedRowCounts: {},
      tableAnswerSet: {},
    });

    queryClient.invalidateQueries({ queryKey: [JPA_QUERY_KEYS.GET_DYNAMIC_TABLE_ANSWERS] });
  },

  setJpaOrgIds: (jpaOrgIds) => set({ jpaOrgIds }),

  setContactAnswer: (contactAnswers, questionId) => {
    set((state) => {
      const otherContacts = state.jpaContactAnswerSet.filter((contact) => contact.questionID !== questionId);

      return {
        jpaContactAnswerSet: [...otherContacts, ...contactAnswers],
      };
    });
  },

  setFullQuestionIds: (fullQuestionIds) => {
    set({ fullQuestionIds });
  },

  setQuestionText: (questionId, questionText) => {
    set((state) => ({
      jpaQuestionText: {
        ...state.jpaQuestionText,
        [questionId]: questionText,
      },
    }));
  },

  setQuestionVisibility: (questionId, isVisible) => {
    set((state) => ({
      jpaQuestionVisibility: {
        ...state.jpaQuestionVisibility,
        [questionId]: isVisible,
      },
    })); // TODO: replace with mutate at later date
  },

  setTableInputAnswer: (data, questionId: number, formId: number) => {
    const formKey = `f${formId}`;
    const questionKey = `q${questionId}`;

    if (data.action === 'update') {
      const cleanValue = typeof data.value === 'string' ? sanitizeText(data.value).trim() : data.value;
      const pageKey = `p${data.pageNumber}`;

      if (get().tableAnswerSet[formKey][questionKey][pageKey][data.rowIndex][data.cellIndex]) {
        set(
          produce((draft) => {
            draft.tableAnswerSet[formKey][questionKey][pageKey][data.rowIndex][data.cellIndex].value = cleanValue;
          })
        );
      } else {
        set(
          produce((draft) => {
            draft.tableAnswerSet[formKey][questionKey][pageKey][data.rowIndex][data.cellIndex] = {
              answerId: 0,
              isDeleted: false,
              questionId,
              value: cleanValue,
            };
          })
        );
      }
    } else if (data.action === 'delete') {
      const pageKey = `p${data.pageNumber}`;
      const currentRow = get().tableAnswerSet[formKey][questionKey][pageKey][data.rowIndex];
      const isNewRow = currentRow.every((cell) => cell?.answerId === undefined || cell?.answerId === 0);

      const calculateRowNumber = (index: number) => {
        return (data.pageNumber - 1) * 10 + index + 1;
      };

      if (isNewRow) {
        // remove row immediately if unsaved
        set(
          produce((draft) => {
            draft.tableAnswerSet[formKey][questionKey][pageKey].splice(data.rowIndex, 1);
          })
        );

        // decrement added row count
        set(
          produce((draft) => {
            const currentCount = draft.tableAddedRowCounts[`f${formId}`]?.[`q${questionId}`] || 0;
            _set(draft.tableAddedRowCounts, [`f${formId}`, `q${questionId}`], currentCount - 1);
          })
        );

        //Update the table to reflect the appropriate row indices.
        set(
          produce((draft) => {
            const touchedPages: string[] = Object.keys(draft.tableAnswerSet[formKey][questionKey]);
            const allRows: JpaTableInputRowData[] = touchedPages
              .map((pageKey) => draft.tableAnswerSet[formKey][questionKey][pageKey])
              .flat();

            allRows.forEach((row) => {
              row.forEach((cell) => {
                // Only reassign rowNumbers greater than deleted rowNumber
                if (cell.rowNumber && cell.rowNumber > calculateRowNumber(data.rowIndex)) {
                  cell.rowNumber = cell.rowNumber - 1;
                }
              });
            });
          })
        );
      } else {
        // otherwise mark saved row as deleted

        const updatedRow = currentRow.map((cell) => ({ ...cell, isDeleted: true }));
        set(
          produce((draft) => {
            draft.tableAnswerSet[formKey][questionKey][pageKey][`${data.rowIndex}`] = updatedRow;
          })
        );
      }

      // remove last page if empty - allows paging to work correctly when adding rows to new page
      const questionPages = Object.keys(get().tableAnswerSet[formKey][questionKey]);
      const lastPageKey = questionPages[questionPages.length - 1];

      if (get().tableAnswerSet[formKey][questionKey][lastPageKey].length === 0) {
        set(
          produce((draft) => {
            delete draft.tableAnswerSet[formKey][questionKey][lastPageKey];
          })
        );
      }
    } else if (data.action === 'undo-delete') {
      const pageKey = `p${data.pageNumber}`;
      const currentRow = get().tableAnswerSet[formKey][questionKey][pageKey][data.rowIndex];
      const updatedRow = currentRow.map((cell) => ({ ...cell, isDeleted: false }));

      set(
        produce((draft) => {
          draft.tableAnswerSet[formKey][questionKey][pageKey][`${data.rowIndex}`] = updatedRow;
        })
      );
    } else if (data.action === 'add') {
      const newRecordPage = Math.ceil((data.totalRowCount + 1) / data.itemsPerPage);

      // add new row to table answer set
      set(
        produce((draft) => {
          const isNewPage = !get().tableAnswerSet?.[formKey]?.[questionKey]?.[`p${newRecordPage}`]?.length;
          const newPage = isNewPage
            ? [data.blankRow]
            : draft.tableAnswerSet[formKey][questionKey][`p${newRecordPage}`].concat([data.blankRow]);

          _set(draft.tableAnswerSet, [formKey, questionKey, `p${newRecordPage}`], newPage);
        })
      );

      // increment added row count
      set(
        produce((draft) => {
          const currentCount = draft.tableAddedRowCounts[`f${formId}`]?.[`q${questionId}`] || 0;
          _set(draft.tableAddedRowCounts, [`f${formId}`, `q${questionId}`], currentCount + 1);
        })
      );
    }
  },
}));
